/**
 * WorkspacePreview Component
 * ==========================
 * Project management preview with board mockup
 * ALL content from Blueprint - NO hardcoded text
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBlueprint, BlueprintSection } from '../../context/BlueprintContext';
import { ArrowRight, Grid3X3, Folder, Image, Plus } from 'lucide-react';

const WorkspacePreview = () => {
  const { homepage, getContent, getRoute } = useBlueprint();
  const workspace = homepage?.workspace || {};
  const mockup = workspace?.mockup || {};
  
  // Get tabs from config with fallback
  const defaultTabs = [
    getContent('workspace.tabs.active', 'Attivi'),
    getContent('workspace.tabs.archived', 'Archiviati'),
    getContent('workspace.tabs.shared', 'Condivisi')
  ];
  const tabs = mockup?.tabs || defaultTabs;
  const projects = mockup?.projects || [];
  
  // Get workspace route from config
  const workspaceRoute = workspace.linkUrl || getRoute('workspace', '/workspace');
  
  const [activeTab, setActiveTab] = useState(0);
  
  // Parse title with newlines
  const renderTitle = () => {
    const title = workspace.title || '';
    return title.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < title.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };
  
  return (
    <BlueprintSection name="workspace">
      <section className="relative overflow-hidden min-h-[500px]">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${workspace.backgroundImage})` }}
        />
        
        {/* Veil */}
        <div className="sr-proj-veil" />
        
        {/* Content */}
        <div className="relative z-10 sr-container py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div>
              <p className="sr-label-gold mb-6">
                {workspace.label}
              </p>
              
              <h2 className="sr-title-section-white mb-6">
                {renderTitle()}
              </h2>
              
              <p className="sr-description-light mb-10 max-w-[420px]">
                {workspace.description}
              </p>
              
              <Link to={workspaceRoute} className="sr-link-gold">
                {workspace.link}
                <ArrowRight size={14} />
              </Link>
            </div>
            
            {/* Right - Board Mockup */}
            <div className="bg-[#141414] rounded-2xl overflow-hidden flex h-[350px] border border-[#2A2A2A] shadow-[0_32px_80px_rgba(0,0,0,0.6)]">
              {/* Sidebar */}
              <div className="w-[54px] bg-[#0C0C0C] flex flex-col items-center py-4 gap-2 border-r border-[#1E1E1E] flex-shrink-0">
                {/* Logo */}
                <div className="w-[30px] h-[30px] border-[1.5px] border-[var(--gold)] flex items-center justify-center font-[var(--ff-h)] text-[8px] font-bold text-[var(--gold)] mb-2.5">
                  SR
                </div>
                
                {/* Icons */}
                <div className="w-9 h-9 rounded-lg bg-[#222] flex items-center justify-center">
                  <Grid3X3 size={15} className="text-[var(--gold)]" />
                </div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center">
                  <Folder size={15} className="text-[#444]" />
                </div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center">
                  <Image size={15} className="text-[#444]" />
                </div>
              </div>
              
              {/* Main */}
              <div className="flex-1 p-5 overflow-hidden">
                {/* Header */}
                <div className="flex justify-between items-center mb-3.5">
                  <span className="text-[#E8E8E8] text-base font-bold">
                    {mockup.title}
                  </span>
                  <button className="inline-flex items-center gap-1.5 text-[11px] text-[var(--gold)] border border-[rgba(169,136,101,0.5)] bg-transparent px-3 py-1.5 rounded-[5px] font-[var(--ff-b)]">
                    <Plus size={12} />
                    {mockup.newButton}
                  </button>
                </div>
                
                {/* Tabs */}
                <div className="flex gap-5 mb-4 pb-2.5 border-b border-[#202020]">
                  {tabs.map((tab, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveTab(i)}
                      className={`text-xs pb-2 border-b-[1.5px] transition-colors font-[var(--ff-b)] ${
                        activeTab === i
                          ? 'text-[var(--gold)] border-[var(--gold)] font-bold'
                          : 'text-[#666] border-transparent'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                
                {/* Project Cards */}
                <div className="grid grid-cols-3 gap-3">
                  {projects.map((project, i) => (
                    <div key={i} className="sr-card-dark">
                      <img
                        src={project.image}
                        alt={project.name}
                        className="w-full h-[78px] object-cover"
                      />
                      <div className="p-2.5 px-3">
                        <div className="text-[#DDD] text-[11.5px] font-bold mb-0.5 font-[var(--ff-b)]">
                          {project.name}
                        </div>
                        <div className="text-[#555] text-[10px] font-[var(--ff-b)]">
                          {project.materials} materiali · {project.time}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </BlueprintSection>
  );
};

export default WorkspacePreview;
